import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth is handled entirely via the backend's httpOnly cookies (sent automatically
// because withCredentials is true). No token is stored in JS-accessible storage.
const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Silent session renewal: if any call returns 401, try POST /auth/refresh once
// (single-flight) using the long-lived refresh cookie, then retry the request.
// Excludes the auth endpoints themselves to avoid loops.
let refreshPromise = null;
const NO_REFRESH = ["/auth/login", "/auth/refresh", "/auth/logout"];

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    const url = original?.url || "";
    const isAuthEndpoint = NO_REFRESH.some((p) => url.includes(p));
    if (status === 401 && original && !original._retry && !isAuthEndpoint) {
      original._retry = true;
      try {
        refreshPromise = refreshPromise || api.post("/auth/refresh");
        await refreshPromise;
        refreshPromise = null;
        return api(original);
      } catch (e) {
        refreshPromise = null;
        if (!url.includes("/auth/me")) redirectToLogin();
        return Promise.reject(error);
      }
    }
    // A 401 we can't recover from (refresh already tried, or it's an auth call
    // other than the initial /auth/me check) means the session is gone.
    if (status === 401 && !url.includes("/auth/me") && !url.includes("/auth/login")) {
      redirectToLogin();
    }
    return Promise.reject(error);
  }
);

function redirectToLogin() {
  if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
    window.location.assign("/login");
  }
}

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
